import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showImages = "showImages",
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
    selectedCategoryIds = "selectedCategoryIds",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImages]: boolean;
        [fields.showOnlyTopLevelCategories]: boolean;
        [fields.selectedCategoryIds]: string[];
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    categoriesDataView: getCategoriesDataView(state),
    topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    titleText?: TypographyPresentationProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
}

export const categoryListStyles: CategoryListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 20,
        css: css`
            margin: 0 auto 30px auto;
            // max-width: 1200px !important;
            > div[class^="GridOffset"] {
                max-width: 100%;
                margin: 0 auto;
                justify-content: center;
            }
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
        `,
    },
    categoryItem: {
        // width: [12, 12, 4, 4, 3],
        css: css`
            padding: 10px;
            flex-basis: 20%;
            max-width: 20%;
            div[class^="GridOffset"] {
                max-width: 100%;
                margin: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 33.3333%;
                        max-width: 33.3333%;
                    `,
                    css`
                        flex-basis: 25%;
                        max-width: 25%;
                    `,
                    css`
                        flex-basis: 20%;
                        max-width: 20%;
                    `,
                ])}
        `,
    },
    innerContainer: {
        gap: 10,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            position: relative;
            padding: 0;
            border: ${getColor("common.border")} 1px solid;
        `,
    },
    categoryImage: {
        width: "100%",
        css: css`
            max-height: 185px;
            min-height: 185px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        max-height: initial;
                        min-height: 300px;
                    `,
                    css`
                        max-height: initial;
                        min-height: 300px;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
            img {
                min-height: 185px;
                max-height: 185px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            max-height: initial;
                            min-height: 300px;
                        `,
                        css`
                            max-height: initial;
                            min-height: 300px;
                        `,
                        null,
                        null,
                        null,
                    ]);
                }}
            }
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 8px;
        `,
    },
    categoryName: {
        css: css`
            color: #363636;
            font-weight: bold;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    categoryImageLink: {
        css: css`
            position: absolute;
            width: 100%;
            bottom: 0;
            left: 0;
            justify-content: space-between;
            display: flex;
            align-items: felx-start;
            background: rgba(24, 36, 61, 0.7);
            padding: 10px;
            height: 60px;
            [data-test-selector="categoryName"] {
                color: #fff;
                font-weight: normal;
                font-size: 18px;
                max-width: 210px;
                display: block;
                line-height: 20px;
                text-transform: capitalize;
                text-shadow: 0px 2px 7px #000;
            }
            .squareIcon {
                background: ${getColor("primary")};
                display: flex;
                height: 30px;
                width: auto;
                padding: 0 5px;
                align-items: center;
                justify-content: center;
                margin-top: 5px;
                > span {
                    height: 20px;
                    width: 20px;
                }
                &:hover {
                    background: #035f9e;
                }
            }
            a {
                &:hover {
                    span[data-test-selector="categoryName"] {
                        color: #d5d5d5;
                    }
                }
            }
        `,
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            font-weight: bold;
            padding-top: 10px;
        `,
    },
};

const styles = categoryListStyles;

class CategoryList extends React.Component<Props> {
    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,
            fields: { title, showImages, showOnlyTopLevelCategories },
        } = this.props;
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }
        const selectedCatIds = this.props.fields.selectedCategoryIds;
        const allCategories = categoriesDataView.value;
        const selectedCategories = allCategories?.filter(el => {
            return selectedCatIds?.some(f => {
                return f === el.id;
            });
        });

        const categories = categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);

        return (
            <>
                <Typography {...styles.titleText}>{title}</Typography>
                <GridContainer {...styles.container}>
                    {selectedCategories.map(category => (
                        <GridItem key={category.id.toString()} {...styles.categoryItem}>
                            <GridContainer {...styles.innerContainer}>
                                {showImages && (
                                    <GridItem {...styles.categoryImageItem}>
                                        {category.path && (
                                            <>
                                                {/*  <LazyImage
                                                    src={category.smallImagePath}
                                                    {...styles.categoryImage}
                                                    altText={category.imageAltText}
                                                /> */}
                                                {category.smallImagePath && (
                                                    <LazyImage
                                                        src={category.smallImagePath}
                                                        {...styles.categoryImage}
                                                    />
                                                )}
                                                {category.smallImagePath === "" && (
                                                    <LazyImage
                                                        src="/UserFiles/Images/Category/SM_NotFound.jpg"
                                                        {...styles.categoryImage}
                                                    />
                                                )}
                                                <StyledWrapper {...styles.categoryImageLink}>
                                                    <Link href={category.path}>
                                                        <Typography
                                                            {...styles.categoryName}
                                                            data-test-selector="categoryName"
                                                        >
                                                            {category.shortDescription}
                                                        </Typography>
                                                    </Link>
                                                    <Link href={category.path} className="squareIcon">
                                                        <Icon src={ChevronRight} size={20} color="#fff" />
                                                    </Link>
                                                </StyledWrapper>
                                            </>
                                        )}
                                    </GridItem>
                                )}
                                {!showImages && (
                                    <GridItem {...styles.categoryNameLinkItem}>
                                        <Link href={category.path} {...styles.categoryNameLink}>
                                            <Typography {...styles.categoryName}>
                                                {category.shortDescription}
                                            </Typography>
                                        </Link>
                                    </GridItem>
                                )}
                                {!showOnlyTopLevelCategories && (
                                    <>
                                        {category.subCategoryIds &&
                                            category.subCategoryIds.slice(0, 4).map(subCategoryId => (
                                                <GridItem key={subCategoryId} {...styles.subCategoryNameLinkItem}>
                                                    <SubCategoryLink categoryId={subCategoryId} />
                                                </GridItem>
                                            ))}
                                        {category.subCategoryIds && category.subCategoryIds.length > 4 && (
                                            <GridItem {...styles.viewMoreLinkItem}>
                                                <Link href={category.path} {...styles.viewMoreLink}>
                                                    {translate("View More")}
                                                </Link>
                                            </GridItem>
                                        )}
                                    </>
                                )}
                            </GridContainer>
                        </GridItem>
                    ))}
                </GridContainer>
            </>
        );
    }
}

const SubCategoryLinkView = ({ category }: ReturnType<typeof linkMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} {...styles.subCategoryNameLink}>
            {category.shortDescription}
        </Link>
    );
};

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryList),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["CategoryListPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOnlyTopLevelCategories,
                displayName: "Show only top-level categories",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
